import { MessageCircle, ArrowRight, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ContactUsHeroSection() {
  const quickContacts = [
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Get instant answers to your questions from our AI assistant & team',
      action: 'Start Chat',
      isChat: true,
      color: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      icon: Calendar,
      title: 'Book a Class',
      description: 'Reserve your spot in upcoming certified yoga and wellness sessions',
      action: 'View Schedule',
      href: '/classes',
      color: 'bg-sky-50 text-sky-700 border-sky-100',
      iconColor: 'text-sky-600'
    },
    {
      icon: Phone,
      title: 'Call Us Now',
      description: 'Speak directly with our dedicated wellness care team in Udaipur',
      action: 'Call +91 88299 12389',
      href: 'tel:+918829912389',
      color: 'bg-amber-50 text-amber-800 border-amber-100',
      iconColor: 'text-amber-600'
    }
  ];

  return (
    <div className="relative overflow-hidden bg-white">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-5 lg:pt-6 pb-4 sm:pb-6">
        <div className="text-center max-w-3xl mx-auto">
          {/* Eyebrow Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200/70 mb-3 shadow-2xs">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
            <span>Contact MetaFit Wellness</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-4">
            Get in <span className="text-emerald-700">Touch</span>
          </h1>

          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Ready to start your wellness journey? We&apos;re here to support you every step of the way. Reach out for classes, consultations, or just to say hello.
          </p>

          {/* Quick Contact Cards */}
          <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
            {quickContacts.map((contact, index) => {
              const IconComponent = contact.icon;
              return (
                <div
                  key={index}
                  className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm hover:shadow-md hover:border-slate-300 hover:-translate-y-1 transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border ${contact.color}`}>
                      <IconComponent size={20} className={contact.iconColor} />
                    </div>
                    <h3 className="mb-1.5 text-lg font-bold text-slate-900">{contact.title}</h3>
                    <p className="mb-4 text-xs sm:text-sm leading-relaxed text-slate-500">{contact.description}</p>
                  </div>

                  {contact.href ? (
                    contact.href.startsWith('tel:') ? (
                      <a
                        href={contact.href}
                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                      >
                        <span>{contact.action}</span>
                        <ArrowRight size={14} />
                      </a>
                    ) : (
                      <Link
                        to={contact.href}
                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                      >
                        <span>{contact.action}</span>
                        <ArrowRight size={14} />
                      </Link>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        const askAiBtn = document.querySelector('[aria-label="Ask AI"]') as HTMLButtonElement;
                        if (askAiBtn) askAiBtn.click();
                      }}
                      className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                    >
                      <span>{contact.action}</span>
                      <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
